import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Reveal, SectionLabel, STATUS_META } from "@/components/common";
import { ProjectCard } from "@/components/ProjectCard";
import { useFetch } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testId}
    className={cn(
      "shrink-0 rounded-full border px-4 py-2 text-xs tracking-wide transition-[background-color,color,border-color] duration-300",
      active ? "border-turq bg-turq text-onturq" : "border-line/20 text-silver hover:border-turq hover:text-fg"
    )}
  >
    {children}
  </button>
);

export default function Work() {
  const { categories, settings } = useSite();
  useSeo({ title: settings && `Work — ${settings.name}`, description: "Brands, campaigns, websites, apps, internal software and AI — shown, not described." });
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const status = params.get("status") || "";
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => input.trim() !== q && setParam("q", input.trim()), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const path = useMemo(() => {
    const sp = new URLSearchParams();
    if (category) sp.set("category", category);
    if (status) sp.set("status", status);
    if (q) sp.set("search", q);
    return `/projects?${sp.toString()}`;
  }, [category, status, q]);
  const { data, loading } = useFetch(path);
  const projects = data || [];
  const reset = () => {
    setInput("");
    setParams({}, { replace: true });
  };

  return (
    <div data-testid="work-page" className="pt-[72px]">
      <section className="container-x pb-12 pt-16 md:pt-24">
        <SectionLabel number="—">Index of work</SectionLabel>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-display text-6xl leading-[0.9] sm:text-7xl lg:text-9xl">Work<span className="text-turq">.</span></h1>
          <p className="max-w-md text-base text-silver md:text-lg">Brands, campaigns, websites, apps, internal software and AI — shown, not described.</p>
        </div>
      </section>

      <section className="sticky top-[72px] z-30 border-y border-line/10 bg-canvas/85 backdrop-blur-xl" aria-label="Filters">
        <div className="container-x space-y-4 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <label className="relative flex min-w-0 flex-1 items-center lg:max-w-sm">
              <span className="sr-only">Search projects</span>
              <Search className="pointer-events-none absolute left-4 h-4 w-4 text-silver" aria-hidden />
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Search name, client, tool, category…"
                data-testid="work-search-input"
                className="w-full rounded-full border border-line/20 bg-transparent py-2.5 pl-11 pr-10 text-sm placeholder:text-silver/70 focus:border-turq focus:outline-none"
              />
              {input && (
                <button type="button" onClick={() => setInput("")} aria-label="Clear search" data-testid="work-search-clear" className="absolute right-3 text-silver hover:text-fg"><X className="h-4 w-4" /></button>
              )}
            </label>
            <div className="no-scrollbar flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
              <Chip active={!status} onClick={() => setParam("status", "")} testId="status-filter-all">All statuses</Chip>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <Chip key={k} active={status === k} onClick={() => setParam("status", status === k ? "" : k)} testId={`status-filter-${k}`}>{m.label}</Chip>
              ))}
            </div>
          </div>
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filter by category">
            <Chip active={!category} onClick={() => setParam("category", "")} testId="category-filter-all">All work</Chip>
            {categories.map((c) => (
              <Chip key={c.slug} active={category === c.slug} onClick={() => setParam("category", category === c.slug ? "" : c.slug)} testId={`category-filter-${c.slug}`}>
                <span className="mr-1.5 font-mono opacity-70">{c.number}</span>{c.name}
              </Chip>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20" aria-live="polite">
        <p className="label-mono mb-10" data-testid="work-results-count">
          {loading && !data ? "Loading…" : `${String(projects.length).padStart(2, "0")} project${projects.length === 1 ? "" : "s"}`}
        </p>
        {!loading && projects.length === 0 ? (
          <div className="flex flex-col items-start gap-6 border-t border-line/10 py-20" data-testid="work-empty-state">
            <p className="font-display text-4xl md:text-6xl">Nothing matches{q ? <> “<em className="text-turq">{q}</em>”</> : " these filters"}.</p>
            <p className="text-silver">Try a different word, or clear the filters to see everything.</p>
            <button type="button" onClick={reset} data-testid="work-reset-filters" className="rounded-full border border-turq px-6 py-3 text-sm text-turq transition-colors hover:bg-turq hover:text-onturq">Reset filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3" data-testid="work-grid">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.07}>
                <ProjectCard project={p} index={i} showTagline />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
